<template>
  <div v-if="hasCompany">
    <horizontal-line class="horizontalLine" />
    <h5>
      {{ i18n.t('/FleetContracts/BlockTitle') }}
      <button @click="showInfoModal = true">
        <icon icon="info-plain" large />
      </button>
    </h5>

    <match-percentage
      v-if="!hasFleetContract && hasRecommendedContract"
      style="float: right"
      :percentage="foundContracts[0].MatchPercentage"
    />

    <modal
      v-if="showInfoModal"
      :title="i18n.t('/FleetContracts/Info/Title')"
      :is-custom-html="true"
      @close="showInfoModal = false"
    >
      <div v-html="i18n.t('/FleetContracts/Info/Text')" />
    </modal>

    <loader v-if="isSearching" />

    <fleet-contract-details v-if="hasFleetContract" :contract="contract" />
    <search-fleet-contract-modal
      :show="showSearchModal"
      :found-contracts="foundContracts"
      :is-searching="isSearching"
      :has-recommended-contract="hasRecommendedContract"
      @search="searchFleetSalesContracts"
      @select="selectContract"
      @close="toggleSearchModal(false)"
    />
    <app-button
      v-if="hasFleetContract"
      data-qa="deselect-fleet-contract-button"
      :disabled="!isEditable || isSearching"
      @click="deselectContract"
      >{{ i18n.t('/FleetContracts/DeselectButton') }}</app-button
    >
    <app-button
      v-else
      :disabled="!isEditable || isSearching"
      data-qa="select-fleet-contract-button"
      @click="toggleSearchModal(true)"
      >{{ i18n.t('/FleetContracts/SelectContract') }}</app-button
    >
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import SearchFleetContractModal from '@/components/generic/presentational/organisms/searchFleetContractModal/SearchFleetContractModal.vue'
import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'
import FleetContractDetails from '@/components/generic/presentational/molecules/fleetContractDetails/FleetContractDetails.vue'
import FleetSalesContracts from '@/api/server/FleetSalesContracts'
import MatchPercentage from '@/components/generic/presentational/atoms/matchPercentage/MatchPercentage.vue'
import moment from 'moment'
import { mapState, mapGetters, mapActions } from 'vuex'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    AppButton,
    SearchFleetContractModal,
    HorizontalLine,
    Icon,
    FleetContractDetails,
    Modal,
    Loader,
    MatchPercentage,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showSearchModal: false,
      showInfoModal: false,
      orderDate: moment.utc().format(),
      isSearching: false,
      foundContracts: [],
    }
  },
  computed: {
    ...mapState('quote', ['quote']),
    ...mapGetters('quote', ['quoteStatusIsSalesOrder', 'hasCustomerCompany']),
    contract() {
      return this.quote?.QuoteDetails?.FleetSalesContract
    },
    hasFleetContract() {
      return this.contract?.ContractNumber != null
    },
    quoteId() {
      return this.quote?.QuoteId
    },
    isEditable() {
      return !this.quoteStatusIsSalesOrder
    },
    hasRecommendedContract() {
      return !!this.foundContracts.find((contract) => contract.MatchPercentage === 100)
    },
    hasCompany() {
      return this.hasCustomerCompany
    },
    poId() {
      return this.quote?.QuoteDetails?.CustomerCompany?.CustomerCompanyPoId
    },
    companyName() {
      return this.quote?.QuoteDetails?.CustomerCompany?.CustomerCompanyName
    },
  },
  watch: {
    hasCompany(newVal) {
      if (!newVal) this.foundContracts = []
    },
    poId(newVal, oldVal) {
      if (newVal !== oldVal && this.hasCompany && !this.hasFleetContract)
        this.searchFleetSalesContracts('', true)
    },
  },
  mounted() {
    if (this.hasCompany && !this.hasFleetContract) {
      this.searchFleetSalesContracts()
    }
  },
  methods: {
    ...mapActions('quote', ['setFleetContract', 'resetFleetContract']),
    deselectContract() {
      FleetSalesContracts.removeContract(this.quoteId)
      this.resetFleetContract()
      this.foundContracts = []
    },
    toggleSearchModal(show) {
      this.showSearchModal = show
    },
    async searchFleetSalesContracts(query = '', openModalOnFullMatch = false) {
      this.isSearching = true
      const ignoreWords = this.i18n.t('/FleetContracts/Search/IgnoreList').split(';')
      let prioritizePoIdMatch = false
      // when invoked without a search query, default search by company name of the quote
      if (query === '' && typeof this.companyName === 'string') {
        prioritizePoIdMatch = true
        for (let namePart of this.companyName.split(' ')) {
          if (!ignoreWords.includes(namePart.toLowerCase())) {
            query = namePart
            break
          }
        }
        // if the company name has only words that should be ignored, search by POID.
        if (query === '') query = this.poId
      }
      const results = await FleetSalesContracts.search(this.orderDate, query, this.quoteId)
      this.isSearching = false

      if (results?.length > 0) {
        this.foundContracts = results.sort((a, b) => {
          if (prioritizePoIdMatch)
            if (
              b.CustomerFleetCompany?.CustomerId === this.poId &&
              a.CustomerFleetCompany?.CustomerId !== this.poId
            ) {
              b.isPoIdMatch = true
              return 1
            } else if (
              b.CustomerFleetCompany?.CustomerId !== this.poId &&
              a.CustomerFleetCompany?.CustomerId === this.poId
            ) {
              a.isPoIdMatch = true
              return -1
            }
          return b.MatchPercentage - a.MatchPercentage
        })

        if (openModalOnFullMatch && this.hasRecommendedContract) this.toggleSearchModal(true)
      }
    },
    async selectContract(contract) {
      this.isSearching = true
      try {
        await FleetSalesContracts.setContract(this.orderDate, contract.ContractNumber, this.quoteId)
        this.setFleetContract(contract)
      } catch (e) {
      } finally {
        this.isSearching = false
        this.toggleSearchModal(false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.horizontalLine {
  margin-top: 1em;
}
</style>
